/**
 * Touch gesture helpers — pinch-to-zoom on the canvas and long-press on any
 * PixiJS Container. Both degrade to no-ops on non-touch devices so callers can
 * wire them unconditionally.
 */
import type { Container, FederatedPointerEvent } from 'pixi.js';

const IS_TOUCH: boolean =
  typeof window !== 'undefined' &&
  ((window.navigator?.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window);

export function isTouchDevice(): boolean {
  return IS_TOUCH;
}

// ─── Pinch zoom ─────────────────────────────────────────────

export interface PinchZoomHandle {
  dispose(): void;
}

export interface PinchZoomOptions {
  /** Minimum movement (in px) between frames before a zoom delta is dispatched. Default 1. */
  threshold?: number;
}

type PinchCallback = (scaleFactor: number, centerX: number, centerY: number) => void;

/**
 * Enable pinch-to-zoom on a canvas (or any HTMLElement). `scaleFactor` is the
 * RATIO between current and previous finger distance (e.g. 1.05 = zoom in 5%).
 * Center coords are in element-local CSS pixels (subtract clientRect).
 */
export function enablePinchZoom(
  el: HTMLElement,
  onZoom: PinchCallback,
  opts: PinchZoomOptions = {},
): PinchZoomHandle {
  if (!IS_TOUCH) return { dispose: () => {} };

  const threshold = opts.threshold ?? 1;
  let lastDist = 0;
  let active = false;

  const dist = (a: Touch, b: Touch) => {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const onStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const t0 = e.touches[0]!;
      const t1 = e.touches[1]!;
      lastDist = dist(t0, t1);
      active = true;
      e.preventDefault();
    }
  };

  const onMove = (e: TouchEvent) => {
    if (!active || e.touches.length !== 2) return;
    const t0 = e.touches[0]!;
    const t1 = e.touches[1]!;
    const d = dist(t0, t1);
    if (Math.abs(d - lastDist) < threshold) return;
    const factor = d / lastDist;
    lastDist = d;
    const rect = el.getBoundingClientRect();
    const cx = (t0.clientX + t1.clientX) / 2 - rect.left;
    const cy = (t0.clientY + t1.clientY) / 2 - rect.top;
    onZoom(factor, cx, cy);
    e.preventDefault();
  };

  const onEnd = (e: TouchEvent) => {
    if (e.touches.length < 2) {
      active = false;
      lastDist = 0;
    }
  };

  el.addEventListener('touchstart', onStart, { passive: false });
  el.addEventListener('touchmove',  onMove,  { passive: false });
  el.addEventListener('touchend',   onEnd);
  el.addEventListener('touchcancel', onEnd);

  return {
    dispose() {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
      el.removeEventListener('touchcancel', onEnd);
    },
  };
}

// ─── Long-press ─────────────────────────────────────────────

export interface LongPressHandle {
  dispose(): void;
}

export interface LongPressOptions {
  /** Hold duration (ms) before firing. Default 500. */
  ms?: number;
  /** Movement tolerance (px) before the press is cancelled. Default 8. */
  moveTolerance?: number;
}

type LongPressCallback = (e: FederatedPointerEvent) => void;

/**
 * Attach a long-press detector to a Pixi Container. Uses federated pointer
 * events so it works for mouse (hold button) and touch. On touch devices the
 * long-press is typically used as a right-click equivalent.
 */
export function enableLongPress(
  target: Container,
  onLongPress: LongPressCallback,
  opts: LongPressOptions = {},
): LongPressHandle {
  const ms = opts.ms ?? 500;
  const tol = opts.moveTolerance ?? 8;
  const tolSq = tol * tol;

  const current = target.eventMode;
  if (!current || current === 'none' || current === 'auto') {
    target.eventMode = 'static';
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;
  let startEvent: FederatedPointerEvent | null = null;

  const cancel = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    startEvent = null;
  };

  const onDown = (e: FederatedPointerEvent) => {
    cancel();
    startX = e.global.x;
    startY = e.global.y;
    startEvent = e;
    timer = setTimeout(() => {
      timer = null;
      if (startEvent) onLongPress(startEvent);
      startEvent = null;
    }, ms);
  };

  const onMove = (e: FederatedPointerEvent) => {
    if (!timer) return;
    const dx = e.global.x - startX;
    const dy = e.global.y - startY;
    if (dx * dx + dy * dy > tolSq) cancel();
  };

  target.on('pointerdown',      onDown);
  target.on('pointermove',      onMove);
  target.on('pointerup',        cancel);
  target.on('pointerupoutside', cancel);
  target.on('pointercancel',    cancel);

  return {
    dispose() {
      cancel();
      target.off('pointerdown',      onDown);
      target.off('pointermove',      onMove);
      target.off('pointerup',        cancel);
      target.off('pointerupoutside', cancel);
      target.off('pointercancel',    cancel);
    },
  };
}
